const STORAGE_KEY = 'magnetic-hound.scores.v1';

export interface ScoreRecord {
  score: number;
  time: number;
  date: string;
}

/** Persistent run records. Best effort: storage may be unavailable. */
export class HighScores {
  private records: ScoreRecord[] = [];

  constructor(private readonly storage: Storage | null = safeStorage()) {
    this.records = load(this.storage);
  }

  get best(): ScoreRecord | null {
    return this.records[0] ?? null;
  }

  get all(): readonly ScoreRecord[] {
    return this.records;
  }

  /** Record a run and report whether it set a new best. */
  submit(score: number, time: number): boolean {
    const record: ScoreRecord = { score, time, date: new Date().toISOString() };
    this.records = [...this.records, record].sort((a, b) => b.score - a.score).slice(0, 10);
    this.persist();
    return this.records[0] === record;
  }

  private persist(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.records));
    } catch {
      // Storage may be unavailable; the run still counts for this session.
    }
  }
}

function load(storage: Storage | null): ScoreRecord[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is ScoreRecord =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as ScoreRecord).score === 'number',
    );
  } catch {
    return [];
  }
}

function safeStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}
